import React from 'react';

/**
 * Pure SVG country flags — reliable rendering across all OS / browsers / fonts.
 * Flat geometric style suitable for inline chips.
 * Supported: KE, UG, CA, GB, US.
 */
export function Flag({ code, size = 18, rounded = true, style }) {
    const w = size;
    const h = Math.round(size * 0.72);
    const r = rounded ? Math.max(2, Math.round(h / 5)) : 0;
    const wrap = {
        display: 'inline-block', verticalAlign: 'middle',
        width: w, height: h, borderRadius: r, overflow: 'hidden',
        boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.08)',
        ...(style || {}),
    };
    return (
        <span style={wrap} aria-label={code} role="img">
            {code === 'KE' && <KenyaSvg />}
            {code === 'UG' && <UgandaSvg />}
            {code === 'CA' && <CanadaSvg />}
            {code === 'GB' && <UnitedKingdomSvg />}
            {code === 'US' && <UnitedStatesSvg />}
        </span>
    );
}

function KenyaSvg() {
    return (
        <svg width="100%" height="100%" viewBox="0 0 60 36" preserveAspectRatio="none">
            <rect width="60" height="12" y="0" fill="#000" />
            <rect width="60" height="1.5" y="12" fill="#fff" />
            <rect width="60" height="9" y="13.5" fill="#BB0A1E" />
            <rect width="60" height="1.5" y="22.5" fill="#fff" />
            <rect width="60" height="12" y="24" fill="#006A4E" />
            {/* Maasai shield (simplified) */}
            <ellipse cx="30" cy="18" rx="4.5" ry="6" fill="#BB0A1E" stroke="#fff" strokeWidth="0.6" />
            <ellipse cx="30" cy="18" rx="2.2" ry="3" fill="#000" />
            {/* spears */}
            <line x1="22" y1="6" x2="38" y2="30" stroke="#fff" strokeWidth="0.7" />
            <line x1="38" y1="6" x2="22" y2="30" stroke="#fff" strokeWidth="0.7" />
        </svg>
    );
}

function UgandaSvg() {
    return (
        <svg width="100%" height="100%" viewBox="0 0 60 36" preserveAspectRatio="none">
            <rect width="60" height="6" y="0" fill="#000" />
            <rect width="60" height="6" y="6" fill="#FCDC04" />
            <rect width="60" height="6" y="12" fill="#D90000" />
            <rect width="60" height="6" y="18" fill="#000" />
            <rect width="60" height="6" y="24" fill="#FCDC04" />
            <rect width="60" height="6" y="30" fill="#D90000" />
            <circle cx="30" cy="18" r="5" fill="#fff" />
            {/* simplified crane silhouette */}
            <path d="M28 18 q1.5 -3 3.5 -3 q1 1 0.5 2 l1.5 1 l-1 1 q-1 2 -2.5 2 z" fill="#9B5A20" />
        </svg>
    );
}

function CanadaSvg() {
    return (
        <svg width="100%" height="100%" viewBox="0 0 60 36" preserveAspectRatio="none">
            <rect width="15" height="36" x="0" fill="#D52B1E" />
            <rect width="30" height="36" x="15" fill="#fff" />
            <rect width="15" height="36" x="45" fill="#D52B1E" />
            {/* Stylized maple leaf */}
            <path
                d="M30 9 L31.6 13.2 L35.5 12.4 L33.3 15.7 L37 17.4 L33.3 19.1 L34.2 22.2 L31 21 L30 25 L29 21 L25.8 22.2 L26.7 19.1 L23 17.4 L26.7 15.7 L24.5 12.4 L28.4 13.2 Z"
                fill="#D52B1E"
            />
        </svg>
    );
}

function UnitedKingdomSvg() {
    return (
        <svg width="100%" height="100%" viewBox="0 0 60 36" preserveAspectRatio="none">
            <rect width="60" height="36" fill="#012169" />
            {/* diagonals (white) */}
            <path d="M0 0 L60 36 M60 0 L0 36" stroke="#fff" strokeWidth="6" />
            {/* diagonals (red, off-center for the saltire) */}
            <path d="M0 0 L60 36" stroke="#C8102E" strokeWidth="2.4" />
            <path d="M60 0 L0 36" stroke="#C8102E" strokeWidth="2.4" />
            {/* cross (white background) */}
            <rect x="24" width="12" height="36" fill="#fff" />
            <rect y="12" width="60" height="12" fill="#fff" />
            {/* cross (red) */}
            <rect x="26.5" width="7" height="36" fill="#C8102E" />
            <rect y="14.5" width="60" height="7" fill="#C8102E" />
        </svg>
    );
}

function UnitedStatesSvg() {
    return (
        <svg width="100%" height="100%" viewBox="0 0 60 36" preserveAspectRatio="none">
            {/* 13 stripes — static SVG geometry, never reorders */}
            {Array.from({ length: 13 }).map((_, i) => (
                <rect key={`stripe-${i}`} x="0" y={i * (36/13)} width="60" height={36/13} fill={i % 2 === 0 ? '#B22234' : '#fff'} />
            ))}
            {/* canton */}
            <rect width="24" height={36 * 7/13} fill="#3C3B6E" />
            {/* stars (5x6 grid of dots, simplified) — static SVG geometry */}
            {Array.from({ length: 5 }).map((_, row) =>
                Array.from({ length: 6 }).map((_, col) => (
                    <circle key={`star-${row}-${col}`} cx={2 + col * 4} cy={2.5 + row * 3.5} r="0.7" fill="#fff" />
                ))
            )}
        </svg>
    );
}

export default Flag;
